<template>
  <div id="app">
    <div id="app-config" style="display:none;">{{ appConfig }}</div>
    <transition name="fade" mode="out-in" appear>
      <router-view></router-view>
    </transition>
  </div>
</template>

<script>
import authService from '@/services/auth';
import companyService from '@/services/company';
import helpers from '@/libs/helpers';
import moment from 'moment-timezone';

/**
 * Main App
 */
export default {
  name: 'app',
  computed: {
    isAuthenticated() {
      return this.$store.getters.idToken !== null;
    },
    idToken() {
      return this.$store.getters.idToken;
    },
    errorFlashs() {
      return this.$store.getters.errorFlashs;
    },
    appConfig() {
      if (window.appConfig) {
        return window.appConfig;
      }
      return {};
    },
  },
  methods: {
    checkTokenExpired() {
      if (this.isAuthenticated) {
        const isExpired = helpers.isTokenExpired(this.$store.getters.idToken);
        if (isExpired) {
          this.$store.dispatch('ADD_ERROR_FLASH', this.$t('4032'));
          // this.$router.push({ name: 'Logout' });
          this.forceLogout();
        }
      }
    },
    updateActiveTimestamp() {
      this.$store.dispatch('UPDATE_ACTIVE_TIMESTAMP');
    },
    checkForceLogout() {
      // get timestamp
      const storedTimestamp = this.$store.getters.activeTimestamp;
      if (storedTimestamp === 0) {
        return
      }
      const now = moment().tz(process.env.DEFAULT_TZ);
      const currentTimestamp = Number(now.format('X'));
      const timeDiff = currentTimestamp - storedTimestamp;
      // check if exceed 10 seconds, force logout
      if (timeDiff > 10) {
        // this.$store.dispatch('ADD_ERROR_FLASH', this.$t('4032'));
        // this.$router.push({ name: 'Logout' });
        this.forceLogout();
      }
    },
    sendCheckSession() {
      // console.log('sendCheckSession', this.isAuthenticated);
      if (this.isAuthenticated) {
        // check if session is expired or not
        // console.log('dispatch CHECK_SESSION');
        this.$store.dispatch('CHECK_SESSION').then(() => {
          // console.log('dispatch CHECK_SESSION_COUNT');
          this.$store.dispatch('CLEAR_SESSION_COUNT');
        }).catch((err) => {
          // console.log('ERROR', err);
          // something fail, should force logout
          if (err) {
            this.$store.dispatch('ADD_ERROR_FLASH', this.$t(err));
          }
          // this.$router.push({ name: 'Logout' });
          this.forceLogout();
        });
      }
    },
    sendHeartbeat() {
      // check if login & have any activities to be sent
      if (this.isAuthenticated) {
        if (this.$store.getters.activityCount > 0) {
          this.$store.dispatch('SEND_HEARTBEAT').then(() => {
          }).catch((err) => {
            // something fail, should force logout
            // this.$message.error(err);
            if (err) {
              this.$store.dispatch('ADD_ERROR_FLASH', this.$t(err));
            }
            this.$router.push({ name: 'Logout' });
          });
        }
        /* else if (this.$store.getters.checkSessionCount < process.env.CHECK_SESSION_COUNT || 5) {
          // check if we have 5 check session count or not
          this.$store.dispatch('ADD_SESSION_COUNT');
        } else {
          // check if session is expired or not
          this.sendCheckSession();
        } */
      }
      if (this.$store.getters.activityCount > 0) {
        this.$store.dispatch('CLEAR_ACTIVITY');
      }
    },
    detectActivity(e) {
      if (this.isAuthenticated) {
        const activity = {
          type: e.type,
          timestamp: Date.now(),
        };
        this.$store.dispatch('ADD_ACTIVITY', activity);
      }
    },
    forceLogout() {
      // this.$router.push({ name: 'Logout' });
      this.$store.dispatch('LOGOUT', {
        vm: this,
      }).then(() => {
        this.$message({
          type: 'success',
          showClose: true,
          duration: 10000,
          message: this.$t('Successfully logged out.'),
        });
        this.$router.push({ name: 'Login' });
      }).catch((err) => {
        // console.error(err);
        this.$log.error(err);
      });
    },
  },
  mounted() {
    this.checkForceLogout();
    // get slug
    const slug = helpers.getURLSlug();
    // we should check tenant_id & app_mode if it is different from the stored one or not
    const token = helpers.getTokenCompanyId(this.$store.getters.idToken);
    if (this.$store.getters.idToken && this.$store.getters.tenantId !== token) {
      // we should logout first
      // window.location.assign(`//${window.location.host}/${slug}/logout`);
      // this.$router.push({ name: 'Logout' });
      this.forceLogout();
    } else {
      this.$store.dispatch('SET_TENANT_ID', slug);
      // set app mode
      this.$store.dispatch('SET_APP_MODE', window.appConfig.SCB_BILLING_APP_MODE);
    }
    // setup heartbeat
    this.sendHeartbeat();
    this.sendCheckSession();
    this.checkTokenExpired();
    setInterval(this.sendHeartbeat, process.env.HEARTBEAT_INTERVAL || 10000);
    setInterval(this.sendCheckSession, process.env.CHECK_SESSION_INTERVAL || 60000);
    // setInterval(this.sendCheckSession, 10000);
    setInterval(this.checkTokenExpired, process.env.CHECK_SESSION_INTERVAL || 60000);
    setInterval(this.updateActiveTimestamp, 1000);
    // add activity capture
    document.addEventListener('click', this.detectActivity);
    document.addEventListener('keyup', this.detectActivity);
    window.addEventListener('load', (event) => {
      const updateOnlineStatus = () => {
        const isOnline = event.type === 'online' || navigator.onLine;
        if (isOnline) {
          this.sendCheckSession();
        }
      };

      window.addEventListener('online', updateOnlineStatus);
      window.addEventListener('offline', updateOnlineStatus);
    });
    // check if we've JWT stored or not
    if (this.$store.getters.idToken) {
      authService.setJWTToken(this.$store.getters.idToken);
      companyService.setJWTToken(this.$store.getters.idToken);
    }
    // send check session to check if current session is valid or not
    this.sendCheckSession();
  },
  watch: {
    idToken(newState, oldState) {
      const isLoggedOut = oldState !== null && newState === null;
      const isLoggedIn = oldState === null && newState !== null;

      if (isLoggedOut) {
        // remove JWT token from each service
        authService.removeJWTToken(this.$store.getters.idToken);
        companyService.removeJWTToken(this.$store.getters.idToken);
        // clear state
        /* this.$store.dispatch('APP_CLEAR_STATE');
        this.$store.dispatch('AUTH_CLEAR_STATE');
        this.$store.dispatch('BILLER_CLEAR_STATE');
        this.$store.dispatch('COMPANY_CLEAR_STATE'); */
        // force go to login page
        this.$router.push({ name: 'Login' });
        // force reload
        // if (process.env.SHOULD_LOGOUT_RELOAD_PAGE) {
        // console.log('FORCE RELOAD');
        window.location.reload();
        // }
      } else if (isLoggedIn) {
        // set JWT token for each service
        authService.setJWTToken(this.$store.getters.idToken);
        companyService.setJWTToken(this.$store.getters.idToken);
        this.$nextTick(() => {
          const goToRouteName = this.$store.getters.isBiller ?
            process.env.DEFAULT_BILLER_ROUTE_NAME :
            process.env.DEFAULT_ROUTE_NAME;
          this.$router.push({ name: goToRouteName });
          // console.log('FORCE RELOAD');
          window.location.reload();
        });
      }
    },
    errorFlashs(newState) {
      if (newState.length > 0) {
        // display all messages
        newState.forEach((errMessage) => {
          this.$message.error({
            // title: 'Error',
            duration: 10000, // stay for 10s
            message: errMessage,
            showClose: true,
            type: 'error',
            // offset: 50,
          });
        });
        // clear message
        this.$store.dispatch('CLEAR_ERROR_FLASH');
      }
    },
  },
};
</script>

<style lang="scss">
/* body {
  margin: 0;
}

#app {
  font-family: 'Avenir', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
}

main {
  text-align: center;
  margin-top: 40px;
}

header {
  margin: 0;
  height: 56px;
  padding: 0 16px 0 24px;
  background-color: #35495E;
  color: #ffffff;
}

header span {
  display: block;
  position: relative;
  font-size: 20px;
  line-height: 1;
  letter-spacing: .02em;
  font-weight: 400;
  box-sizing: border-box;
  padding-top: 16px;
} */
</style>



// WEBPACK FOOTER //
// src/App.vue